/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 **/

import { Client as StompClient, StompConfig, IMessage, StompSubscription, IFrame } from "@stomp/stompjs";
import { EventEmitter } from "eventemitter3";

import { EventHandler } from "@rfice/eventhandler";
import { NetworkEvents } from "./useStompClient";
import { Properties } from "../properties";
import { Configure, Utility } from "../utilities";

const LOGTAG = "#StompNetwork#";

interface INetwork<T> {
	connect(options: NetworkConnectOptions<StompConfig>, topics: AllTopics): Promise<void>;
	disconnect(): void;
	subscribe(topics: AllTopics, addTopics?: boolean, callback?: (() => void) | null): void;
	unsubscribe(topics: AllTopics): void;
	publish<T>(topics: Topic, type: Message.Types, payload: T, options: PublishOptions): void;
	active: boolean;
	isConnected: boolean;
	client: T | null;
	clientOptions: StompConfig;
	masterMode: boolean;
	topics: Topics;
	instances: { [name: string]: INetwork<T> };
}

export class NetworkStatic extends EventEmitter implements INetwork<StompClient> {
	constructor(
		private _client: StompClient | null = null,
		private _clientOptions: StompConfig = {
			onConnect: (frame) => {
				// console.log("hsshin, onConnect", frame, this);
				this.isPrintable && console.debug(LOGTAG, "onConnect", this.isConnected);
				EventHandler.emit(NetworkEvents.onConnected, frame);
				this.emit(NetworkEvents.onConnected, frame);
				this.emit(`${NetworkEvents.onConnected}_once`);
			},
			onDisconnect: (frame) => {
				this.isPrintable && console.debug(LOGTAG, "onDisconnect", frame);
				EventHandler.emit(NetworkEvents.onDisconnect, frame);
				this.emit(NetworkEvents.onDisconnect, frame);
			},
			onChangeState: (frame) => {
				console.debug(LOGTAG, "onChangeState", frame);
			},
			onStompError: (frame: IFrame) => {
				this.isPrintable && console.debug(LOGTAG, "onStompError", frame, this.isConnected, this.client);
				EventHandler.emit(NetworkEvents.onStompError, frame);
				this.emit(NetworkEvents.onStompError, frame);
			},
			onWebSocketClose: (frame) => {
				console.debug(LOGTAG, "onWebSocketClose", frame, this.isConnected, this.client);
				this.emit("$$disconnected");
				EventHandler.emit(NetworkEvents.onWebSocketClosed, frame);
				this.emit(NetworkEvents.onWebSocketClosed, frame);
			},
			onWebSocketError: (frame: IFrame) => {
				console.debug(LOGTAG, "onWebSocketError", frame);
				EventHandler.emit(NetworkEvents.onConnectFailure, frame);
				this.emit(NetworkEvents.onConnectFailure, frame);
			},
			onUnhandledReceipt: (frame) => {
				console.debug(LOGTAG, "onUnhandledReceipt", frame);
			},
			onUnhandledFrame: (frame) => {
				console.debug(LOGTAG, "onUnhandledFrame", frame);
			},
			onUnhandledMessage: (frame) => {
				console.debug(LOGTAG, "onUnhandledMessage", frame);
			}
		},
		private _activateTopics: ActivateTopic[] = [],
		private _master: boolean = true,
		public instances: { [name: string]: NetworkStatic } = {}
	) {
		super();
		this.instances["default"] = this;
	}

	private _subscription: Record<string, StompSubscription> = {};

	public get isPrintable() {
		return Configure.useLogLevel >= 7;
	}

	public get isConnected(): boolean {
		try {
			return Boolean(this.client?.connected);
		} catch {
			return false;
		}
	}
	public get client(): StompClient | null {
		return this._client;
	}

	public get clientOptions(): StompConfig {
		return this._clientOptions;
	}
	public set clientOptions(value: StompConfig) {
		this._clientOptions = Object.assign({}, this._clientOptions, value);
	}
	public get masterMode(): boolean {
		return this._master;
	}
	public set masterMode(mode: boolean) {
		this._master = mode;
	}

	public get topics(): Topics {
		return this._activateTopics.map((t) => t.topic);
	}

	public get active(): boolean {
		return this.client?.active || false;
	}

	public hasTopics(topics: string[]): boolean {
		return this._activateTopics.some((topic) => topics.indexOf(topic.topic) >= 0);
	}

	public connect(options: NetworkConnectOptions<StompConfig>, topics: AllTopics = []): Promise<void> {
		return new Promise((resolve, reject) => {
			this._activateTopics = [];
			this.start(options)
				.then(() => {
					this.isPrintable && console.debug(LOGTAG, "websocket connected", topics);
					this.subscribe(topics);
					resolve();
				})
				.catch((error) => {
					reject(error);
				});
		});
	}

	private start(options: NetworkConnectOptions<StompConfig>): Promise<void> {
		this.clientOptions = options;
		return this.connectToServer(this.clientOptions);
	}

	private connectToServer(options: NetworkConnectOptions<StompConfig>) {
		return new Promise<void>((resolve) => {
			if (!this.client) {
				this._client = new StompClient({ ...options });
			}
			if (this.client) {
				this.client.activate();
			}
			this.waitForConnect().then(resolve);
		});
	}
	public waitForConnect(): Promise<void> {
		return new Promise<void>((resolve) => {
			this.once(`${NetworkEvents.onConnected}_once`, () => {
				this.removeListener(`${NetworkEvents.onConnected}_once`);
				resolve();
			});
		});
	}

	public async disconnect(): Promise<void> {
		await this.disconnectFromServer();
		this.clear();
		console.debug(LOGTAG, "disconnected");
	}

	public clear() {
		this._client = null;
		this._activateTopics = [];
	}

	private async disconnectFromServer() {
		return new Promise<void>((resolve) => {
			if (this.client) {
				this.once("$$disconnected", () => {
					resolve();
				});
				this.client.deactivate().then(() => {});
			}
		});
	}

	public isSubscribed(topic: string) {
		return this._activateTopics.findIndex((t) => t.topic === topic) >= 0;
	}

	public subscribe(topics: AllTopics, addTopics = true, callback: (() => void) | null = null): void {
		try {
			if (!this.client) {
				throw Error("Client is null");
			}
			if (Array.isArray(topics)) {
				console.groupCollapsed(LOGTAG, "topic subscribing...");
				topics.forEach((topic) => this.subscribe(topic, addTopics, null));
				console.groupEnd();
				callback?.();
			} else {
				const abstractTopic = typeof topics === "function" ? topics() : topics;
				const topicString: Topic = isString(abstractTopic) ? abstractTopic : abstractTopic?.topic ?? "";
				const topicObject: TopicObject = isTopicObject(abstractTopic)
					? abstractTopic
					: { topic: abstractTopic ?? "", permanent: false };
				// console.debug(LOGTAG, "subscribe", abstractTopic, topicString && !this.isSubscribed(topicString));

				if (abstractTopic) {
					if (this.isSubscribed(topicString)) {
						this._activateTopics = this._activateTopics.map((t) =>
							t.topic === topicString ? { ...t, refCount: t.refCount + 1 } : t
						);
						// console.debug(LOGTAG, "subscribe2 already exist..after", this._activateTopics);
					} else {
						this._activateTopics.push({
							topic: topicString,
							permanent: topicObject.permanent || false,
							refCount: 1
						});
						// console.debug(LOGTAG, "subscribe3", this._activateTopics);
						this._subscription[topicString] = this.client.subscribe(
							topicString,
							this.onMessageReceived.bind(this)
						);
						EventHandler.emit(NetworkEvents.onSubscribe, abstractTopic, null);
						callback?.();
					}
				}
			}
		} catch (error) {
			console.warn(LOGTAG, "subscribe failed with", topics, error);
		}
	}

	private onMessageReceived(frame: IMessage) {
		const message = Utility.convert.stringToJSON(frame.body) as Message.Frame;
		EventHandler.emit(NetworkEvents.onMessageReceived, frame.headers.destination, frame, message);
	}

	public unsubscribe(topics: Exclude<AllTopics, TopicObject | TopicObject[]>, ingoreRefCount = true): void {
		if (this.client && this.isConnected) {
			if (Array.isArray(topics)) {
				topics.forEach((topic) => this.unsubscribe(topic));
			} else {
				const topic = typeof topics === "function" ? topics() : topics;
				if (topic && this._activateTopics.findIndex((t) => t.topic === topic) >= 0) {
					const isPermanentTopic = Boolean(this._activateTopics.find((t) => t.topic === topic)?.permanent);
					if (isPermanentTopic) {
						console.warn(LOGTAG, "permanent topic cannot be unsubscribed", topic);
						return;
					}
					const _activeTopic = this._activateTopics.find((t) => t.topic === topic);
					// console.debug(LOGTAG, "unsubscribe", topic, isPermanentTopic, _activeTopic);
					if (!_activeTopic) {
						return;
					}
					if (ingoreRefCount || _activeTopic?.refCount <= 1) {
						const subscription = this._subscription[topic];
						if (subscription) {
							subscription.unsubscribe();
							delete this._subscription[topic];
						}
						{
							const index = this._activateTopics.findIndex((t) => t.topic === topic);
							index >= 0 && this._activateTopics.splice(index, 1);
						}
						EventHandler.emit(NetworkEvents.onUnsubscribe, topic, null);
					} else {
						this._activateTopics = this._activateTopics.map((t) =>
							t.topic === topic ? { ...t, refCount: t.refCount - 1 } : t
						);
					}
				}
			}
		} else {
			throw new Error("Method not implemented.");
		}
	}

	public publish<T>(
		topics: Topic,
		type: Message.Types,
		payload: T,
		options: Pick<PublishOptions, "includeSelf"> = {}
	): void {
		if (this.client) {
			if (this.isConnected) {
				const message: Message.Frame<Message.Types, T> = {
					type,
					payload,
					...(options?.includeSelf === false
						? {
								options: {
									clientUniqueID: Properties.browserTabID
								}
							}
						: {})
				};
				const packet = JSON.stringify(message);
				const send = (topic: Topic) => {
					// this.isPrintable && console.debug(LOGTAG, "in send", { destination: topic, body: packet });
					this.client?.publish({
						destination: topic,
						body: packet,
						headers: { "content-type": "application/json" }
					});
				};
				if (Array.isArray(topics)) {
					topics.forEach((topic) => send(topic));
				} else {
					send(topics);
				}
			}
		} else {
			throw new Error("Client is null");
		}
	}
}

const Network = new NetworkStatic();
export default Network;

function isTopicObject(topic: unknown): topic is TopicObject {
	return typeof topic === "object" && topic !== null && "topic" in topic;
}

function isString(topic: unknown): topic is string {
	return typeof topic === "string";
}
